import DataGrid, { Column, HeaderFilter, Paging, SearchPanel, Editing, Sorting, Selection, GroupPanel, Grouping, ColumnChooser, Toolbar, Item, Lookup } from 'devextreme-react/data-grid';
import React, { useCallback, useRef, useState, memo, useMemo, useEffect } from 'react';
import useWhyDidYouUpdate from '../../modules/useWhyDidYouUpdate';
import { CheckBox } from 'devextreme-react/check-box';
import WebWorker from '../../modules/WebWorker';
import workerModule from './Tabela01.worker';
import PropTypes from 'prop-types';
import './Tabela01.css';


const SelectionColumnComponent = () => (
    <Column type='selection' width={30} ></Column>
);
const SelectionColumn = memo(SelectionColumnComponent);

function Tabela01(props){

    const { columns, data, emptyMessage = '', className, navigationOnKeyBoard=false, onRowClick, setQtdReg, allowFocusRow, allowGrouping, fullHeight,
    disableHeaderFilter, disableSearchPanel, deleteModalTarget, editModalTarget, setRowValues, allowSelection, extraToolbarAlignment,
    allowColumnChooser, extraToolbarContent, keysNumber=1, height='', width, columnAutoWidth=false, pageSize=200, customColumns, changeFocusOnPageChange=false, onEditorPreparing=()=>{},
    onKeyDown, batchEdit, onRowUpdated, prefocus, onCellClick, customActionButtons=[], saveOnEnter, onRowValidating=()=>{}, onCellDblClick=()=>{},
    onSelectionChanged=()=>{}, onRowDblClick=()=>{}, disableEditOnDblClick=false, allowEditingRow=()=>true, allowDeletingRow=()=>true,
    keepSelection=false, keepSorting=false, keepFilter=false, disableSorting=false, onContentReady } = props;

    if (props.inspectRender) useWhyDidYouUpdate('Tabela01', props);

    const editButton = useRef();
    const tableRef = useRef();
    
    const [focusedRowIndex, setFocusedRowIndex] = useState(prefocus ? 0 : -1);
    const [allTrue, setAllTrue] = useState({});
    const worker = new WebWorker(workerModule);

    useEffect(() => {
        !keepSelection && tableRef.current.instance.clearSelection();
        !keepSorting && tableRef.current.instance.clearSorting();
        !keepFilter && tableRef.current.instance.clearFilter();
    }, [data]);

    const keys = useMemo(() => (
        columns.filter((col, index) => index < keysNumber).map(col => col.datafield)
    ), [columns]);
    
    const markAll = useCallback(async (e) => {
        const column = columns[columns.map(col => col?.datafield).indexOf(e?.column?.dataField)];
        if (e?.rowType === 'header' && column?.allowMarkAll) {
            const changes = e.component.option('editing.changes');
            const val = !allTrue[e.column.dataField] || false;
            
            e.component.beginUpdate();

            const chaves = keys;

            worker.postMessage({datafield: e.column.dataField, val: val, data, chaves, changes});
            worker.addEventListener('message', async event => {
                const newChanges = event.data;
                const setAllTruePromise = () => new Promise((res, rej) => {
                    setAllTrue({ ...allTrue, [e.column.dataField]: val });
                    setTimeout(() => {
                        res();
                    }, 0);
                });
                await setAllTruePromise();
                e.component.option('editing.changes', newChanges);
                
                worker.terminate();
                e.component.endUpdate();
            });
        }
    }, [allTrue]);
    
    const rowClick = useCallback((e) => {
        onRowClick && onRowClick(e);
    }, [onRowClick]);
    
    const unfocusRows = useCallback(() => {
        setFocusedRowIndex(changeFocusOnPageChange ? 0 : -1);
    }, [setFocusedRowIndex]);

    const onCellPrepared = useCallback((e) => {
        if(e?.rowType === 'header'){
            function searchColumn(columns, dataField, caption) {
                if (!columns) return null;
                for (let col of columns) {
                    if (col.datafield === dataField && col.caption === caption) return col;
                    if (col.columns) {
                        const found = searchColumn(col.columns, dataField, caption);
                        if (found) return found;
                    }
                }
                return null;
            };
            const column = searchColumn(columns, e.column.dataField, e.column.caption);
            e.cellElement.title = column?.title || column?.caption;
        }
    }, [columns]);

    const cellClick = useCallback((e) => {
        e.event.preventDefault();
        try {
            markAll(e);
            rowClick(e.row);
            onCellClick && onCellClick(e);
        }
        catch {
            // console.log('Erro na função cellClick, verificar o código da Tabela01')
        }
    }, [onCellClick, rowClick, unfocusRows, allTrue]);

    const handleSetRowValues = useCallback((data) => {
        setRowValues(Object.keys(data).reduce((acc, key) => {
            return { ...acc, [key]: (data[key] || '') };
        }, {}));
    }, []);
    const actionModalButtons = useCallback((row) => (
        <div className='container-fluid d-flex justify-content-center'>
            { customActionButtons.map((button, index) => ( (!button.allowCustomActionRow || button.allowCustomActionRow(row)) ?
                <a key={index} title={button.title} className={'dx-icon-' + button.icon + ' dx-link dx-link-icon border-none shadow-none btn btn-link'} onClick={event => button.onClick(row.data, event)} data-bs-toggle={button.modalTarget ? 'modal' : undefined} data-bs-target={button.modalTarget}></a>
                :
                <span style={{ width: 14 }}/>
            ))}
            { editModalTarget ? (allowEditingRow(row) ? <a role='button' title='Editar' className='dx-link dx-icon-edit dx-link-icon border-none shadow-none btn btn-link' onClick={() => handleSetRowValues(row.data)} data-bs-toggle='modal' data-bs-target={`#${editModalTarget}`}></a> : <span style={{ width: 14 }}/>) : <></>}
            { deleteModalTarget ? (allowDeletingRow(row) ? <a role='button' title='Excluir' className='dx-link dx-icon-trash dx-link-icon border-none shadow-none btn btn-link text-danger' onClick={() => handleSetRowValues(row.data)} data-bs-toggle='modal' data-bs-target={`#${deleteModalTarget}`}></a> : <span style={{ width: 14 }}/>) : <></>}
        </div>
    ), [setRowValues, editModalTarget, deleteModalTarget, customActionButtons]);

    const markAllCellRender = useCallback((datafield, caption) => {
        return(
            <span className='container-fluid d-flex align-items-center justify-content-center' title={caption} role='button'>
                <CheckBox value={!!allTrue[datafield]} className='me-2' />
                {caption.slice(0, 1)}
            </span>
        );
    }, [allTrue]);

    const handleDblRowClick = useCallback((e) => {
        editModalTarget && !disableEditOnDblClick && !handleSetRowValues(e.data) && editButton.current.click();
        onRowDblClick(e);
    }, [onRowDblClick, editModalTarget, actionModalButtons]);

    const handleFocusChange = useCallback((e) => {
        if(!navigationOnKeyBoard) {
            setFocusedRowIndex(e.rowIndex);
            rowClick(e.row);
        } 
    }, [setFocusedRowIndex, rowClick, navigationOnKeyBoard]);

    const setAlignment = useCallback(({ dataType, alignment }) => {
        if(dataType === 'money') return 'right';
        else if(dataType === 'percentage') return 'center';
        return alignment;
    }, []);

    const setDataType = useCallback(({ dataType }) => {
        if(['decimal', 'money', 'percentage'].includes(dataType)) return 'number';
        return dataType;
    }, []);

    const setCellRender = useCallback(({ dataType, cellRender, moneyDigits=2 }) => {
        if(cellRender) return cellRender;
        if(dataType === 'money') return e => 'R$' + Number(e.value || e.text).toLocaleString(undefined, { minimumFractionDigits: moneyDigits, maximumFractionDigits: moneyDigits });
        if(dataType === 'percentage') return e => <span className='percentage-cell' style={{'--p': `${(typeof e.value === 'number') ? e.value : 0}%`}}><span>{ typeof e.value === 'number' ? Number(e.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00' }%</span></span>;
        return cellRender;
    }, []);

    const setFormat = useCallback(({ dataType, format, decimal=3 }) => {
        if(dataType === 'decimal') return { type: 'fixedPoint', precision: decimal, editorType: 'dxDateBox' };
        return format;
    }, []);
    
    const setEditorOptions = useCallback(({ dataType, format, maxLength }) => {
        if(dataType === 'date') return { useMaskBehavior: true, openOnFieldClick: true };
        if(maxLength) return { maxLength: maxLength };
        if(dataType === 'money') return { format: value => `R$${Number(value || '0').toLocaleString('pt-BR', { maximumFractionDigits: 2 })}` };
        if(dataType === 'percentage') return { format: value => `${Number(value || '0').toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`, min: 0, max: 100 };
        else return format;
    }, []);

    const handleContentReady = useCallback(() => {
        const gridInstance = tableRef?.current?.instance;
        if(navigationOnKeyBoard) {
            gridInstance.option('focusedRowIndex', 0);
            setTimeout(() => gridInstance.focus(gridInstance.getCellElement(0)), 200);
        }
        if(prefocus) {
            gridInstance.focus(gridInstance.getCellElement(0));
        }
        if(setQtdReg) {
            setQtdReg(gridInstance.getDataSource()?._totalCount);
        };
        if (onContentReady) onContentReady(gridInstance);
    }, [setQtdReg, navigationOnKeyBoard]);

    const renderColumn = useCallback((column) => {
        if (column.columns) return (
            <Column caption={column.caption} alignment={column.alignment} fixed={column.fixed}>
                { column.columns.map((subColumn) => renderColumn(subColumn)) }
            </Column>
        );
        if (column.allowMarkAll) return (
            <Column dataField={column.datafield} width={column.width}
            defaultSortOrder={column.defaultSortOrder} alignment={column.alignment} 
            allowHeaderFiltering={false} allowSorting={false}
            headerCellComponent={() => markAllCellRender(column.datafield, column.caption)}
            key={column.datafield} dataType={column.dataType} visible={!column.hide} 
            minWidth={column.minWidth} allowGrouping={column.allowGrouping || false} 
            allowEditing={ column.allowEditing || false } />
        );
        if (column.selectEditing) return (
            <Column dataField={column.datafield} caption={column.caption} width={column.width}
            defaultSortOrder={column.defaultSortOrder} alignment={setAlignment(column)} cssClass={`select-cell editable-cell ${column.cssClass || ''}`}
            allowHeaderFiltering={!column.disableHeaderFilter} cellRender={setCellRender(column)}
            key={column.datafield} dataType={setDataType(column)} visible={!column.hide}
            minWidth={column.minWidth} allowGrouping={column.allowGrouping || false} allowSorting={!disableSorting} fixed={column.fixed}
            format={setFormat(column)} allowReordering={column.allowReordering} editorOptions={setEditorOptions(column)}
            showInColumnChooser={!column.hideInColumnChooser} calculateSortValue={column.calculateSortValue} >
                <Lookup dataSource={column.selectEditing}/>
            </Column>
        );
        return (
            <Column dataField={column.datafield} caption={column.caption} width={column.width}
            defaultSortOrder={column.defaultSortOrder} alignment={setAlignment(column)} 
            allowHeaderFiltering={!column.disableHeaderFilter} cellRender={setCellRender(column)}
            key={column.datafield} dataType={setDataType(column)} visible={!column.hide}
            minWidth={column.minWidth} allowGrouping={column.allowGrouping || false} 
            cssClass={`${(column.allowEditing && batchEdit) ? 'editable-cell' : ''} position-relative ${column.cssClass || ''}`}
            allowEditing={column.allowEditing || false} allowSorting={!disableSorting} fixed={column.fixed}
            format={setFormat(column)} allowReordering={column.allowReordering} editorOptions={setEditorOptions(column)}
            showInColumnChooser={!column.hideInColumnChooser} calculateSortValue={column.calculateSortValue}/>
        );
    }, [ columns, allTrue, batchEdit]);

    const columnsRender = useMemo(() => (
        columns.map(column => renderColumn(column))
    ), [columns, allTrue, batchEdit]);

    function handleKeyDownCapture(e) {
        if (['ArrowDown', 'ArrowUp'].includes(e.key) && e?.target?.nodeName === 'INPUT') {
            e.stopPropagation();
            e.target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
        }
    }

    return(
        <div className={`user-select-none ${className}${fullHeight ? ' datagrid-full-height' : ''}`} onKeyDownCapture={handleKeyDownCapture}>
            <DataGrid showBorders noDataText={emptyMessage || 'Nenhum registro encontrado'} dataSource={data} onRowDblClick={handleDblRowClick}
            focusedRowEnabled={allowFocusRow} onPagingChanging={unfocusRows} renderAsync loadPanel={false}
            onEditorPreparing={e => e.parentType === 'dataRow' && onEditorPreparing(e)} className={batchEdit ? 'editable-grid' : undefined}
            autoNavigateToFocusedRow={false} onCellClick={cellClick} height={height} width={width} columnAutoWidth={columnAutoWidth} onKeyDown={onKeyDown} columnResizingMode='widget'
            onFocusedRowChanged={handleFocusChange} keyExpr={keys} onRowUpdated={onRowUpdated} allowColumnResizing onCellPrepared={onCellPrepared}
            keyboardNavigation={{enabled: true, editOnKeyPress: true}} onEditCanceled={() => setAllTrue({})} onCellDblClick={onCellDblClick}
            ref={tableRef} onRowValidating={onRowValidating} onSelectionChanged={onSelectionChanged} onContentReady={handleContentReady} >
                <GroupPanel visible={allowGrouping} emptyPanelText='Arraste colunas mais escuras até aqui para agrupar os dados...' />
                <Grouping autoExpandAll={false} />
                <Sorting />
                { (batchEdit && <Editing useIcons mode='batch' allowUpdating={e => allowEditingRow(e?.row?.data)} selectTextOnEditStart startEditAction='dblClick' onChangesChange={() => saveOnEnter ? tableRef.current.instance.saveEditData() : undefined} />)
                || <Editing useIcons /> }
                <Paging defaultPageSize={pageSize} onPageIndexChange={unfocusRows} pageSize={pageSize} />
                <SearchPanel visible={!disableSearchPanel} placeholder='Filtro' onTextChange={unfocusRows} />
                <HeaderFilter visible={!disableHeaderFilter} search={{enabled: true}} />
                <ColumnChooser enabled={allowColumnChooser} emptyPanelText='Arraste colunas até aqui para escondê-las...' title='Esconder colunas' width={275} />

                { allowSelection && <Selection mode='multiple' showCheckBoxesMode='always' /> }

                { customColumns } 
                { allowSelection && 
                    // <SelectionColumn />
                    <Column type='selection' fixed fixedPosition='left' width={40}></Column>
                }
                { (((deleteModalTarget || editModalTarget) && setRowValues) || customActionButtons.length > 0) &&
                    <Column type='buttons' caption='Ações' cellRender={actionModalButtons} allowGrouping={false} fixed fixedPosition='left' ></Column>
                }
                
                { columnsRender }
                
                <Toolbar visible={allowColumnChooser || !disableSearchPanel || extraToolbarContent || allowGrouping || (batchEdit && !saveOnEnter)}>
                    { allowColumnChooser && <Item name='columnChooserButton' cssClass={className} /> }
                    { !disableSearchPanel && <Item name='searchPanel' /> }
                    { extraToolbarContent && <Item location={extraToolbarAlignment}> {extraToolbarContent()} </Item> }
                    { allowGrouping && <Item name='groupPanel' /> }
                    { batchEdit && <Item name='saveButton' visible={!saveOnEnter} /> }
                    { batchEdit && <Item name='revertButton' visible={!saveOnEnter} /> }
                </Toolbar>
            </DataGrid>
            { editModalTarget && !disableEditOnDblClick && <button className='d-none' ref={editButton} data-bs-toggle='modal' data-bs-target={`#${editModalTarget}`} ></button> }
        </div>
    );
}

Tabela01.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        dataType: PropTypes.oneOf(['string', 'number', 'date', 'boolean', 'object', 'datetime', 'percentage', 'decimal', 'money']),
        selectEditing: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
        minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        alignment: PropTypes.oneOf(['center', 'left', 'right']),
        defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
        datafield: PropTypes.string.isRequired,
        hideInColumnChooser: PropTypes.bool,
        disableHeaderFilter: PropTypes.bool,
        calculateSortValue: PropTypes.func,
        allowReordering: PropTypes.bool,
        onCellDblClick: PropTypes.func,
        allowGrouping: PropTypes.bool,
        moneyDigits: PropTypes.number,
        allowEditing: PropTypes.bool,
        allowMarkAll: PropTypes.bool,
        cellRender: PropTypes.func,
        cssClass: PropTypes.string,
        caption: PropTypes.string,
        decimal: PropTypes.number,
        format: PropTypes.object,
        title: PropTypes.string,
        fixed: PropTypes.bool,
        hide: PropTypes.bool,
    }).isRequired).isRequired,
    customActionButtons: PropTypes.arrayOf(PropTypes.shape({
        allowCustomActionRow: PropTypes.func,
        onClick: PropTypes.func,
        icon: PropTypes.string,
        title: PropTypes.string,
        modalTarget: PropTypes.string,
    })),
    customColumns: PropTypes.arrayOf(PropTypes.object),

    data: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    keysNumber: PropTypes.number,
    prefocus: PropTypes.bool,
    
    extraToolbarAlignment: PropTypes.string,
    deleteModalTarget: PropTypes.string,
    editModalTarget: PropTypes.string,
    setRowValues: PropTypes.func,
    
    changeFocusOnPageChange: PropTypes.bool,
    disableEditOnDblClick: PropTypes.bool,
    navigationOnKeyBoard: PropTypes.bool,
    disableHeaderFilter: PropTypes.bool,
    disableSearchPanel: PropTypes.bool,
    allowColumnChooser: PropTypes.bool,
    allowSelection: PropTypes.bool,
    disableSorting: PropTypes.bool,
    allowGrouping: PropTypes.bool,
    allowFocusRow: PropTypes.bool,
    keepSelection: PropTypes.bool,
    keepSorting: PropTypes.bool,
    saveOnEnter: PropTypes.bool,
    keepFilter: PropTypes.bool,
    fullHeight: PropTypes.bool,
    batchEdit: PropTypes.bool,
    
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    columnAutoWidth: PropTypes.bool,
    pageSize: PropTypes.number,
    
    onSelectionChanged: PropTypes.func,
    onEditorPreparing: PropTypes.func,          // e.component.closeEditCell fecha a edição da célula
    allowDeletingRow: PropTypes.func,
    allowEditingRow: PropTypes.func,
    onRowValidating: PropTypes.func,
    onContentReady: PropTypes.func,
    onRowDblClick: PropTypes.func,
    onRowUpdated: PropTypes.func,
    onCellClick: PropTypes.func,
    onRowClick: PropTypes.func,
    onKeyDown: PropTypes.func,

    extraToolbarContent: PropTypes.func,
    className: PropTypes.string,
};

export default memo(Tabela01);